import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { SuperAdminLayout } from './SuperAdminLayout';
import { useNavigate } from 'react-router-dom';
import {
  Eye, EyeOff, AlertTriangle, Building2, LogOut, Shield
} from 'lucide-react';

const SuperAdminImpersonation = () => {
  const { api, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [impersonating, setImpersonating] = useState(null);

  useEffect(() => {
    const stored = sessionStorage.getItem('impersonation');
    if (stored) setImpersonating(JSON.parse(stored));
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const res = await api.get('/super-admin/companies?limit=200');
      setCompanies(res.data.companies);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleImpersonate = async (companyId) => {
    if (!window.confirm(t('saImpersonation.confirmEnter'))) return;
    try {
      const res = await api.post(`/super-admin/impersonate/${companyId}`);
      const data = res.data;
      const session = {
        session_id: data.session_id,
        company: data.company,
        token: data.token,
        original_token: localStorage.getItem('token'),
        started_at: new Date().toISOString()
      };
      sessionStorage.setItem('impersonation', JSON.stringify(session));
      localStorage.setItem('token', data.token);
      setImpersonating(session);
      window.location.href = '/dashboard';
    } catch (err) {
      alert(err.response?.data?.detail || t('saImpersonation.errorGeneric'));
    }
  };

  const handleStopImpersonation = async () => {
    const session = JSON.parse(sessionStorage.getItem('impersonation') || '{}');
    try {
      localStorage.setItem('token', session.original_token);
      await api.post('/super-admin/stop-impersonation', { session_id: session.session_id });
    } catch (err) { console.error(err); }
    sessionStorage.removeItem('impersonation');
    setImpersonating(null);
    window.location.href = '/super-admin';
  };

  const filtered = companies.filter(c =>
    !search || c.name.toLowerCase().includes(search.toLowerCase()) || (c.email || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <SuperAdminLayout>
      <div className="space-y-4" data-testid="super-admin-impersonation">
        <div>
          <h1 className="text-xl font-bold text-white">{t('saImpersonation.title')}</h1>
          <p className="text-sm text-gray-500">{t('saImpersonation.subtitle')}</p>
        </div>

        {/* Active impersonation banner */}
        {impersonating && (
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 flex items-center justify-between" data-testid="impersonation-active-banner">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              <div>
                <p className="text-sm font-bold text-amber-400">{t('saImpersonation.activeBannerTitle')}</p>
                <p className="text-xs text-gray-400">{t('saImpersonation.connectedAs', { name: impersonating.company?.name })}</p>
              </div>
            </div>
            <button onClick={handleStopImpersonation} data-testid="stop-impersonation-btn" className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-black rounded-lg text-sm font-semibold hover:bg-amber-400">
              <LogOut className="w-4 h-4" /> {t('saImpersonation.stopSupport')}
            </button>
          </div>
        )}

        {/* Warning */}
        <div className="bg-red-500/5 border border-red-500/10 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs text-red-300 font-semibold">{t('saImpersonation.warningTitle')}</p>
              <p className="text-xs text-gray-500 mt-1">{t('saImpersonation.warningText')}</p>
            </div>
          </div>
        </div>

        {/* Search */}
        <input
          type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder={t('saImpersonation.searchPlaceholder')}
          data-testid="impersonate-search"
          className="w-full px-4 py-3 bg-[#111827] border border-white/10 rounded-xl text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#009b3a]/40"
        />

        {/* List */}
        <div className="space-y-2">
          {loading ? (
            <div className="p-8 text-center"><div className="w-6 h-6 border-2 border-[#009b3a] border-t-transparent rounded-full animate-spin mx-auto" /></div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-sm text-gray-600 py-8">{t('saImpersonation.emptyList')}</p>
          ) : (
            filtered.map(c => (
              <div key={c.id} className="bg-[#111827] border border-white/5 rounded-xl p-4 flex items-center justify-between" data-testid={`impersonate-company-${c.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#009b3a]/10 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-[#009b3a]" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-white">{c.name}</p>
                    <p className="text-xs text-gray-500">{t('saImpersonation.companyMeta', { email: c.email, plan: c.subscription_plan })}</p>
                  </div>
                </div>
                <button
                  onClick={() => handleImpersonate(c.id)}
                  data-testid={`impersonate-btn-${c.id}`}
                  className="flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/20 text-amber-400 rounded-lg text-xs font-semibold hover:bg-amber-500/20 transition-all"
                >
                  <Eye className="w-3.5 h-3.5" /> {t('saImpersonation.enterBtn')}
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </SuperAdminLayout>
  );
};

export default SuperAdminImpersonation;
